import { WalletPayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { useWalletQuery } from '../../api/wallet/getWallet';
import colors from '../../constants/colors';
import paymentTypeMap, {
  getPaymentCommonName,
} from '../../constants/paymentTypeNames';
import useAnalytics from '../../hooks/useAnalytics';
import useGetCustKey from '../../hooks/useGetCustKey';
import { usePaymentsContext } from '../../store/payments';
import { setSelectedPaymentMethod } from '../../store/paymentsReducer';
import { getWalletAccount, getWalletCard } from '../../utils/wallet';
import { EditButton } from '../styledComponents/IconButtons';
import CircularProgress from '../CircularProgress/CircularProgress';

interface WalletSelectionProps {
  setSelectedPaymentType: (method: 'EFT' | 'CRDC') => void;
  selectedPaymentAccountToken: string;
  setSelectedPaymentAccountToken: (token: string) => void;
}

const WalletSelection = ({
  setSelectedPaymentType,
  setSelectedPaymentAccountToken,
  selectedPaymentAccountToken,
}: WalletSelectionProps) => {
  const { dispatch, state } = usePaymentsContext();
  const location = useLocation();
  const custKey = useGetCustKey();
  const { data: wallet, isLoading, error } = useWalletQuery(custKey);
  const { track } = useAnalytics();
  const [focusedPaymentOption, setFocusedPaymentOption] = useState<
    string | null
  >(null);

  const walletAccounts: WalletPayment[] = wallet?.accounts ?? [];

  const walletEmpty = !isLoading && (error || walletAccounts.length === 0);

  useEffect(() => {
    if (selectedPaymentAccountToken && wallet?.accounts) {
      const payment = wallet?.accounts?.find(
        (account) => account.paymentAccountToken === selectedPaymentAccountToken
      );
      if (!payment) return;
      const card = getWalletCard(payment);
      const account = getWalletAccount(payment);
      const accountType = card?.type ?? account?.type ?? '';
      dispatch(
        setSelectedPaymentMethod({
          analyticsAccountType: getPaymentCommonName(accountType),
          name: card?.holderName ?? account?.holderName ?? '',
          paymentToken: payment.paymentAccountToken,
          paymentMethod: card ? 'CRDC' : 'EFT',
          expirationDate: card?.expirationDate ?? '',
          zipCode: card?.zipCode ?? '',
          displayPaymentMethod: `${paymentTypeMap[accountType]} ****${
            card?.number ?? account?.number
          }`,
        })
      );
    }
  }, [selectedPaymentAccountToken]);

  const getAccountLabel = (account: WalletPayment): string => {
    if (account.shortName) {
      return account.shortName;
    }

    const card = getWalletCard(account);
    const bankAccount = getWalletAccount(account);
    if (card) {
      return `${paymentTypeMap[card.type]} ****${card.number}`;
    } else if (bankAccount) {
      return `${paymentTypeMap[bankAccount.type]} ****${bankAccount.number}`;
    } else {
      return 'Payment Method';
    }
  };

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          width: 1
        }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  return (
    <FormControl sx={{ mt: 2 }}>
      <Stack spacing={2}>
        {walletEmpty && <Navigate to="add" replace state={location.state} />}
        {walletAccounts.map((account: WalletPayment, idx) => {
          const card = getWalletCard(account);
          const cardExpired = card ? card.expired : false;
          const accountLabel = getAccountLabel(account);
          return (
            <FormControlLabel
              sx={{
                border:
                  selectedPaymentAccountToken === account.paymentAccountToken
                    ? '2px solid black'
                    : `1px solid ${colors.csaaNeutralBorder}`,
                ...(focusedPaymentOption === account.paymentAccountToken && {
                  borderColor: colors.csaaLight,
                }),
                borderRadius: 1,
                mx: 0,
                fontFamily: 'Cabin',
                fontSize: 18,
                fontWeight:
                  selectedPaymentAccountToken === account.paymentAccountToken
                    ? 700
                    : 400,
                cursor: cardExpired ? 'default' : 'initial',
                outline:
                  focusedPaymentOption === account.paymentAccountToken
                    ? `2px solid ${colors.csaaBlack}`
                    : 'none',
                outlineOffset: 2,
                '&:hover': {
                  background: colors.csaaOverlayBlack,
                  borderColor: colors.csaaMid,
                  color: colors.csaaMid,
                },
              }}
              key={`${account.shortName}-${idx}`}
              data-testid={accountLabel}
              value={account.paymentAccountToken}
              control={
                <Radio
                  onFocus={() =>
                    setFocusedPaymentOption(account.paymentAccountToken)
                  }
                  onBlur={() => setFocusedPaymentOption(null)}
                  disabled={cardExpired}
                  sx={{
                    color: colors.csaaMid,
                    '&.Mui-checked': {
                      color: colors.csaaMid,
                    },
                  }}
                  checked={
                    selectedPaymentAccountToken === account.paymentAccountToken
                  }
                  disableRipple
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (card) {
                      setSelectedPaymentType('CRDC');
                    } else {
                      setSelectedPaymentType('EFT');
                    }

                    setSelectedPaymentAccountToken(event.target.value);
                  }}
                  slotProps={{
                    input: {
                      'aria-label': getAccountLabel(account),
                    }
                  }}
                />
              }
              label={
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    width: 1
                  }}>
                  <Stack
                    className="csaa-qm-block"
                    direction="row"
                    sx={{
                      alignItems: { mobile: 'flex-start', tablet: 'center' },
                      gap: { mobile: 1, tablet: 2 },
                      cursor: cardExpired ? 'default' : 'pointer'
                    }}>
                    {accountLabel}
                    {cardExpired && (
                      <Box
                        sx={{
                          backgroundColor: colors.csaaRed10,
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          height: 24,
                          alignSelf: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'Cabin',
                            fontKerning: 'normal',
                            fontSize: 14,
                            lineHeight: '16px',
                            letterSpacing: 0.2,
                            color: colors.csaaDark,
                            fontWeight: 500,
                          }}
                        >
                          Expired
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <EditButton
                    aria-label={`Edit payment method ${getAccountLabel(
                      account
                    )}`}
                    component={Link}
                    to="edit"
                    state={{ walletAccount: account, edit: true }}
                    sx={{
                      mr: 2,
                    }}
                    onClick={() => {
                      track('Payment Methods Accessed', {
                        event_type: 'Link Accessed',
                        location: 'Feature Card',
                        link: 'Pencil icon',
                        policy_number: state.singleSelectedPayment.policyNumber,
                        state: state.singleSelectedPayment.riskState,
                      });
                    }}
                  />
                </Stack>
              }
              disableTypography
            />
          );
        })}
      </Stack>
    </FormControl>
  );
};

export default WalletSelection;
